import React from 'react';
import { Button } from 'reactstrap';

/**
 * Props for the Footer component.
 * @property [renderFooterButtons] - Optional custom renderer for footer buttons.
 * @property handleNext - Function to handle moving to the next step.
 * @property handlePrev - Function to handle moving to the previous step.
 * @property currentStep - The index of the current step.
 * @property stepCount - Total number of steps in the wizard.
 * @property isStepValid - Indicates whether the current step is valid.
 */
interface ComponentProps {
  renderFooterButtons?: (
    handleNext: () => void,
    handlePrev: () => void,
    currentStep: number,
    stepCount: number,
  ) => React.ReactNode;
  handleNext: () => Promise<void>;
  handlePrev: () => Promise<void>;
  currentStep: number;
  stepCount: number;
  isStepValid: boolean;
  isSubmitting: boolean;
  handleSubmit: (event?: any) => void;
  handleCancel?: () => void;
  showPrevButton?: boolean;
  prevButtonCaption?: string;
  showNextButton?: boolean;
  nextButtonCaption?: string;
  disableNext?: boolean;
  submitCaption?: string;
}

/**
 * Renders the footer buttons for the wizard form.
 *
 * This component can display either custom footer buttons through the
 * `renderFooterButtons` prop or default "Previous" and "Next" buttons.
 *
 * @param  props
 * @returns The rendered footer buttons.
 */
export const Footer: React.FC<ComponentProps> = ({
  renderFooterButtons,
  handleNext,
  handlePrev,
  currentStep,
  stepCount,
  isStepValid,
  isSubmitting,
  handleSubmit,
  handleCancel,
  showPrevButton = true,
  prevButtonCaption = 'Previous',
  showNextButton = true,
  nextButtonCaption = 'Next',
  disableNext = false,
  submitCaption = 'Submit',
}) => {
  const disableSubmit = !isStepValid || isSubmitting;

  return (
    <div className="wizard-footer">
      {renderFooterButtons ? (
        renderFooterButtons(handleNext, handlePrev, currentStep, stepCount)
      ) : (
        <div className="footer-buttons-wrapper">
          {handleCancel && (
            <Button color="danger" type="button" onClick={handleCancel} className="cancel-button">
              Cancel
            </Button>
          )}

          <div className="d-flex">
            {currentStep > 0 && showPrevButton && (
              <Button color="secondary" onClick={handlePrev} className="me-2">
                {prevButtonCaption}
              </Button>
            )}

            {currentStep === stepCount - 1
              ? showNextButton && (
                  <Button
                    color="success"
                    disabled={disableSubmit}
                    type="button"
                    onClick={handleSubmit}
                    className="submit-button"
                  >
                    {submitCaption}
                  </Button>
                )
              : showNextButton && (
                  <Button type="button" color="success" onClick={handleNext} className="next-button" disabled={disableNext}>
                    {nextButtonCaption}
                  </Button>
                )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Footer;
